const CONSENT_POLL_INTERVAL = 250;
const COOKIE_SCRIPT_LOADER_ID = "cookie-script-loader";
const COOKIE_SCRIPT_LOADER_SELECTOR = `#${COOKIE_SCRIPT_LOADER_ID}`;
const COOKIE_SCRIPT_SRC = "https://cdn.cookie-script.com/s/f1f55eb0ae82d9f1b389bc635c667150.js";
const COOKIE_SCRIPT_DOM_SELECTOR = [
  "#cookiescript_badge",
  'style[data-type="cookiescriptstyles"]'
].join(", ");
const COOKIE_SCRIPT_SETTINGS_SELECTOR = ".cookie-script-settings-button";
const COOKIE_SCRIPT_DIALOG_TIMEOUT = 500;
const COOKIE_SCRIPT_INSTANCE_TIMEOUT = 3000;
const COOKIE_SCRIPT_INSTANCE_POLL_INTERVAL = 50;
const COOKIE_SCRIPT_CONSENT_EVENT_NAMES = [
  "CookieScriptLoaded",
  "CookieScriptAcceptAll",
  "CookieScriptAccept",
  "CookieScriptReject"
];
const consentChangeSubscribers = new Set();
let consentPollInterval = null;
let consentRecheckTimeout = null;
let consentStateFallbackInstance = null;
let cookieScriptLoadPromise = null;

document.addEventListener("click", handleCookieScriptLinkClick, true);
document.addEventListener("turbo:before-render", preserveCookieScriptDom);

// Returns true only when CookieScript has accepted every requested category.
// Callers must pass the category explicitly so consent requirements stay visible.
export function hasCookieScriptConsent(category) {
  const requiredCategories = normalizeCategoryList(category);
  if (requiredCategories.length === 0) {
    return false;
  }

  return hasConsentForCategories(requiredCategories);
}

// Opens the CookieScript settings dialog, loading the CMP runtime if needed.
// Load failures are treated as a no-op so embeds remain blocked.
export function showCookieScriptSettings() {
  const instance = window.CookieScript && window.CookieScript.instance;
  if (!instance) {
    ensureCookieScriptLoaded().then(openCookieScriptSettings);
    return;
  }

  if (hasCookieScriptDialog()) {
    openCookieScriptSettings(instance);
    return;
  }

  // CookieScript recreates a missing dialog asynchronously. Wait for its
  // existing runtime to finish before falling back to a forced reload.
  recoverCookieScriptDialog(instance).then((recovered) => {
    if (recovered) {
      openCookieScriptSettings(instance);
    } else {
      ensureCookieScriptLoaded({ force: true }).then(openCookieScriptSettings);
    }
  });
}

function recoverCookieScriptDialog(instance) {
  if (typeof instance.show !== "function") {
    return Promise.resolve(false);
  }

  try {
    instance.show();
  } catch (_error) {
    return Promise.resolve(false);
  }

  return waitForCookieScriptDialog();
}

function waitForCookieScriptDialog() {
  return new Promise((resolve) => {
    const deadline = Date.now() + COOKIE_SCRIPT_DIALOG_TIMEOUT;

    const poll = () => {
      if (hasCookieScriptDialog()) {
        resolve(true);
        return;
      }

      if (Date.now() >= deadline) {
        resolve(false);
        return;
      }

      window.setTimeout(poll, COOKIE_SCRIPT_INSTANCE_POLL_INTERVAL);
    };

    poll();
  });
}

function openCookieScriptSettings(instance) {
  if (!instance) {
    return;
  }

  try {
    if (typeof instance.showDetails === "function") {
      instance.showDetails();
    } else if (typeof instance.show === "function") {
      instance.show();
    }
  } catch (_error) {
    // A stale third-party instance must not break the app-owned settings link.
  }
}

function hasCookieScriptDialog() {
  return Boolean(document.getElementById("cookiescript_injected"));
}

// Handles only our app-owned Privacy Settings links. CookieScript's own banner
// controls must keep their native handlers, otherwise accept/decline can stop
// closing the CMP banner even though the network request succeeds.
function handleCookieScriptLinkClick(event) {
  if (!(event.target instanceof Element)) {
    return;
  }

  const settingsControl = event.target.closest(COOKIE_SCRIPT_SETTINGS_SELECTOR);
  if (!settingsControl) {
    return;
  }

  event.preventDefault();
  event.cookieScriptSettingsHandled = true;
  showCookieScriptSettings();
}

// Used only when a user explicitly asks to open CookieScript settings and the
// CMP runtime/dialog is missing. We avoid reloading on Turbo navigation because
// replacing CookieScript while its banner is active can leave stale CMP UI on
// the page.
function ensureCookieScriptLoaded({ force = false } = {}) {
  if (!force && window.CookieScript && window.CookieScript.instance) {
    return Promise.resolve(window.CookieScript.instance);
  }

  if (cookieScriptLoadPromise) {
    return cookieScriptLoadPromise;
  }

  const previousRuntime = force ? window.CookieScript : null;
  document.querySelector(COOKIE_SCRIPT_LOADER_SELECTOR)?.remove();
  if (force) {
    consentStateFallbackInstance = previousRuntime && previousRuntime.instance;
    resetCookieScriptRuntime();
  }

  const script = document.createElement("script");
  script.id = COOKIE_SCRIPT_LOADER_ID;
  script.type = "text/javascript";
  script.charset = "UTF-8";
  script.src = COOKIE_SCRIPT_SRC;

  cookieScriptLoadPromise = new Promise((resolve) => {
    let settled = false;

    const settle = (instance) => {
      if (settled) {
        return;
      }

      settled = true;
      if (!instance) {
        restoreCookieScriptRuntime(previousRuntime);
      }

      if (window.CookieScript && window.CookieScript.instance) {
        consentStateFallbackInstance = null;
      }
      cookieScriptLoadPromise = null;
      resolve(instance);
    };

    const finish = () => {
      waitForCookieScriptInstance().then(settle);
    };

    script.addEventListener("load", finish, { once: true });
    script.addEventListener("error", () => settle(null), { once: true });
  });

  document.head.append(script);

  return cookieScriptLoadPromise;
}

function resetCookieScriptRuntime() {
  try {
    delete window.CookieScript;
  } catch (_error) {
    window.CookieScript = undefined;
  }
}

function restoreCookieScriptRuntime(runtime) {
  if (!runtime || (window.CookieScript && window.CookieScript.instance)) {
    return;
  }

  try {
    window.CookieScript = runtime;
  } catch (_error) {
    // currentCookieScriptState can keep using the fallback instance.
    consentStateFallbackInstance = runtime.instance;
  }
}

function waitForCookieScriptInstance() {
  return new Promise((resolve) => {
    const deadline = Date.now() + COOKIE_SCRIPT_INSTANCE_TIMEOUT;
    let timeoutId = null;

    const cleanup = () => {
      window.removeEventListener("CookieScriptLoaded", handleLoaded);
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };

    const resolveWithInstance = () => {
      const instance = window.CookieScript && window.CookieScript.instance;
      if (!instance) {
        return false;
      }

      cleanup();
      resolve(instance);
      return true;
    };

    const poll = () => {
      if (resolveWithInstance()) {
        return;
      }

      if (Date.now() >= deadline) {
        cleanup();
        resolve(null);
        return;
      }

      timeoutId = window.setTimeout(poll, COOKIE_SCRIPT_INSTANCE_POLL_INTERVAL);
    };

    const handleLoaded = () => {
      window.setTimeout(resolveWithInstance, 0);
    };

    window.addEventListener("CookieScriptLoaded", handleLoaded);
    poll();
  });
}

// Turbo replaces the body during navigation. Keep CookieScript's badge and style
// node so the CMP remains styled, but never carry #cookiescript_injected between
// pages because that banner is interactive state owned by CookieScript.
function preserveCookieScriptDom(event) {
  const newBody = event.detail && event.detail.newBody;
  if (!newBody) {
    return;
  }

  document.querySelectorAll(COOKIE_SCRIPT_DOM_SELECTOR).forEach((element) => {
    removeMatchingCookieScriptElement(newBody, element);
    newBody.append(element);
  });
}

function removeMatchingCookieScriptElement(newBody, element) {
  if (element.id) {
    Array.from(newBody.querySelectorAll("[id]"))
      .find((candidate) => candidate.id === element.id)
      ?.remove();
  } else {
    newBody.querySelector('style[data-type="cookiescriptstyles"]')?.remove();
  }
}

// Notifies a subscriber only when consent for all of its requested categories
// changes between granted and denied.
export function subscribeToCookieScriptConsentChanges(category, callback) {
  if (typeof callback !== "function") {
    throw new TypeError("CookieScript consent subscriber must be a function");
  }

  const requiredCategories = normalizeCategoryList(category);
  if (requiredCategories.length === 0) {
    return () => {};
  }

  const subscriber = {
    callback,
    hasConsent: hasConsentForCategories(requiredCategories),
    requiredCategories
  };

  consentChangeSubscribers.add(subscriber);
  startConsentMonitoring();

  let subscribed = true;

  return () => {
    if (!subscribed) {
      return;
    }

    subscribed = false;
    consentChangeSubscribers.delete(subscriber);
    stopConsentMonitoringIfIdle();
  };
}

// Safely reads CookieScript's current state. Any unavailable or failing CMP
// state returns null, which callers treat as missing consent.
function currentCookieScriptState() {
  const instance = (window.CookieScript && window.CookieScript.instance) || consentStateFallbackInstance;
  if (!instance || typeof instance.currentState !== "function") {
    return null;
  }

  try {
    return instance.currentState();
  } catch (_error) {
    return null;
  }
}

// Normalizes CookieScript's accepted category array for case-insensitive checks.
function normalizeCategories(categories) {
  if (!Array.isArray(categories)) {
    return [];
  }

  return normalizeCategoryList(categories);
}

// Supports one or more required categories passed as comma/space-separated text.
function normalizeCategoryList(category) {
  const categoryValues = Array.isArray(category) ? category : [category];

  return categoryValues.flatMap((value) => {
    if (typeof value !== "string") {
      return [];
    }

    return value
      .split(/[,\s]+/)
      .map((categoryName) => categoryName.trim().toLowerCase())
      .filter(Boolean);
  });
}

function hasConsentForCategories(requiredCategories) {
  const state = currentCookieScriptState();
  if (!state) {
    return false;
  }

  const acceptedCategories = new Set(normalizeCategories(state.categories));
  return requiredCategories.every((category) => acceptedCategories.has(category));
}

function startConsentMonitoring() {
  if (consentPollInterval) {
    return;
  }

  COOKIE_SCRIPT_CONSENT_EVENT_NAMES.forEach((eventName) => {
    window.addEventListener(eventName, handlePotentialConsentChange);
  });

  consentPollInterval = window.setInterval(notifyChangedConsentSubscribers, CONSENT_POLL_INTERVAL);
}

function stopConsentMonitoringIfIdle() {
  if (consentChangeSubscribers.size > 0 || !consentPollInterval) {
    return;
  }

  COOKIE_SCRIPT_CONSENT_EVENT_NAMES.forEach((eventName) => {
    window.removeEventListener(eventName, handlePotentialConsentChange);
  });

  window.clearInterval(consentPollInterval);
  consentPollInterval = null;

  if (consentRecheckTimeout) {
    window.clearTimeout(consentRecheckTimeout);
    consentRecheckTimeout = null;
  }
}

function handlePotentialConsentChange() {
  notifyChangedConsentSubscribers();

  if (consentRecheckTimeout) {
    return;
  }

  // Some CookieScript releases update currentState() after dispatching their
  // event. Recheck once after the event stack without notifying twice.
  consentRecheckTimeout = window.setTimeout(() => {
    consentRecheckTimeout = null;
    notifyChangedConsentSubscribers();
  }, 0);
}

function notifyChangedConsentSubscribers() {
  const state = currentCookieScriptState();
  const acceptedCategories = new Set(normalizeCategories(state && state.categories));

  consentChangeSubscribers.forEach((subscriber) => {
    const hasConsent = Boolean(state) && subscriber.requiredCategories.every(
      (category) => acceptedCategories.has(category)
    );

    if (hasConsent === subscriber.hasConsent) {
      return;
    }

    subscriber.hasConsent = hasConsent;
    subscriber.callback();
  });
}
